<template>
    <div>
        <div class="d-flex justify-content-between align-items-end mb-2">
            <div>
                <h4>Request Interview Order</h4>
            </div>
        </div>

        <validation-observer ref="validationObserver" v-slot="vo">
            <!--Ordering Client / Client Reference-->
            <b-form-row>
                <b-form-group label="Ordering Client"
                              label-for="orderingClient"
                              class="col-md-4">
                    <b-form-input name="orderingClient"
                                  maxlength="500"
                                  :disabled="true"
                                  v-model="orderingClient">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Ordering Client Contact"
                              label-for="orderingClientContact"
                              class="col-md-4">
                    <b-form-input name="orderingClientContact"
                                  :disabled="true"
                                  v-model="orderingClientContact">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Client Reference"
                              label-for="clientReference"
                              class="col-md-4">
                    <b-form-input name="clientReference"
                                  maxlength="500"
                                  :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                  v-model="clientReference">
                    </b-form-input>
                    <b-tooltip target="clientReference" triggers="hover" placement="bottom" variant="dark" v-if="clientReference">
                        {{ clientReference }}
                    </b-tooltip>
                </b-form-group>
            </b-form-row>
            <b-form-row>
                <validation-provider rules="required" name="Ordering Client Branch/Location" v-slot="vp" slim>
                    <b-form-group label="Ordering Client Branch/Location"
                                  label-for="orderingClientBranchId"
                                  class="col-md-4">
                        <select2 name="orderingClientBranchId"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 :options="selectableClientBranches"
                                 key-property="clientBranchId"
                                 text-property="name"
                                 placeholder="Select a client branch"
                                 v-model="orderingClientBranchId"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group class="col-md-4">
                    <b-form-checkbox name="separateBillingContact"
                                     v-model="separateBillingContact"
                                     class="mb-2"
                                     switch
                                     :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC">
                        Separate billing contact
                    </b-form-checkbox>
                    <validation-provider rules="required" name="Billing Contact" v-slot="vp" v-if="separateBillingContact">
                        <contact-select name="billingContactId"
                                        :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                        :api-url="$urlHelper.getApiUrl('contact', 'billing')"
                                        :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                        :mandatory-contact-properties="mandatoryContactProperties"
                                        :client-id="orderingClientId"
                                        v-model="billingContactId"
                                        :initial-contacts="initialBillingContact ? [ initialBillingContact ] : null"
                                        :state="vp.errors[0] ? false : null">
                        </contact-select>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </validation-provider>
                </b-form-group>
                <b-form-group label="Request Number"
                              label-for="orderRequestNumber"
                              class="col-md-2">
                    <b-form-input name="orderRequestNumber"
                                  :value="isUuidNullOrEmpty(orderRequest.clientOrderRequestId) ? null : orderRequest.orderRequestNumberDisplayText"
                                  readonly>
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Status"
                              label-for="orderRequestStatus"
                              class="col-md-2">
                    <b-form-input name="orderRequestStatus"
                                  :value="isUuidNullOrEmpty(orderRequest.clientOrderRequestId) ? null : orderRequest.statusDisplayName"
                                  readonly>
                    </b-form-input>
                </b-form-group>
            </b-form-row>
            <!--Interview Organisation-->
            <b-form-row>
                <validation-provider ref="interviewOrganisationValidator" rules="required" name="Interview Organisation" v-slot="vp" slim>
                    <b-form-group label="Interview Organisation"
                                  label-for="interviewOrganisationId"
                                  class="col-md-4">
                        <select2 name="interviewOrganisationId"
                                 :disabled="isUuidNullOrEmpty(orderingClientId) || readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 v-model="interviewOrganisationId"
                                 :options="allInterviewOrganisations"
                                 disable-search
                                 placeholder="Select an Interview Organisation"
                                 v-on:select="interviewOrganisationSelected"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>
            <!--Location / Title-->
            <b-form-row>
                <b-form-group label="Interview Location"
                              label-for="interviewLocation"
                              class="col-md-4">
                    <b-form-input name="interviewLocation"
                                  maxlength="500"
                                  :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                  v-model="interviewLocation">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Title of related Matter or Case"
                              label-for="interviewMatterOrCaseReference"
                              class="col-md-6">
                    <b-form-input name="interviewMatterOrCaseReference"
                                  :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                  v-model="interviewMatterOrCaseReference">
                    </b-form-input>
                </b-form-group>
            </b-form-row>
            <!--Dates / Length-->
            <b-form-row>
                <validation-provider ref="interviewDateValidator" rules="required" name="Date/Time of Interview" v-slot="vp" slim>
                    <b-form-group label="Date/Time of Interview"
                                  label-for="interviewDateTime"
                                  class="col-md-4">
                        <ejs-datetimepicker name="interviewDateTime"
                                            v-model="interviewDateTime"
                                            format="d/M/yyyy h:mm:ssa"
                                            timeFormat="hh:mm:ssa"
                                            :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"></ejs-datetimepicker>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider rules="required" name="Time zone of Interview" v-slot="vp" slim>
                    <b-form-group label="Time zone of Interview"
                                  label-for="interviewTimezoneId"
                                  class="col-md-6">
                        <select2 name="interviewTimezoneId"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 v-model="interviewTimezoneId"
                                 :options="allTimeZones"
                                 placeholder="Select a time zone"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group label="Length"
                              label-for="interviewLengthInSeconds"
                              class="col-md-2">
                    <audio-length-input name="interviewLengthInSeconds"
                                        v-model="interviewLengthInSeconds"
                                        :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC">
                    </audio-length-input>
                </b-form-group>
            </b-form-row>
            <!--Service Type / Service Delay-->
            <b-form-row>
                <validation-provider ref="serviceTypeSelectValidator" rules="required" name="Service Type" v-slot="vp" slim>
                    <b-form-group label="Service Type"
                                  label-for="serviceTypeId"
                                  class="col-md-4">
                        <select2 ref="serviceTypeSelect"
                                 name="serviceTypeId"
                                 v-model="serviceTypeId"
                                 :options="currentSelectableServiceTypes"
                                 disable-search
                                 key-property="serviceTypeId"
                                 text-property="name"
                                 :placeholder="interviewOrganisationId ? 'Select a service type' : 'Select an organisation first'"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC || (interviewOrganisationId == null)"
                                 v-on:select="serviceTypeSelected"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider ref="serviceDelaySelectValidator" rules="required" name="Turnaround" v-slot="vp" slim v-if="serviceTypeRequiresServiceDelay">
                    <b-form-group label="Turnaround"
                                  label-for="serviceDelayId"
                                  class="col-md-4">
                        <select2 ref="serviceDelaySelect"
                                 name="serviceDelayId"
                                 v-model="serviceDelayId"
                                 :options="currentSelectableServiceDelays"
                                 disable-search
                                 key-property="serviceDelayId"
                                 text-property="name"
                                 placeholder="Select a service delay"
                                 :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>
            <!--Interview Parties-->
            <interview-parties title="Interviewer/s"
                               edit-dialog-title="Interviewer Details"
                               :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                               v-model="interviewers"
                               class="mb-2">
            </interview-parties>
            <interview-parties title="Interviewee/s"
                               edit-dialog-title="Interviewee Details"
                               :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                               v-model="interviewees"
                               class="mb-2">
            </interview-parties>
            <!--Default Distribution List-->
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <b-form-group label="Distribution List"
                              label-for="deliveryContactIds"
                              class="col">
                    <contact-select name="deliveryContactIds"
                                    :api-url="$urlHelper.getApiUrl('contact','delivery')"
                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                    :client-id="orderingClientId"
                                    :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                    v-model="currentDeliveryContactIds"
                                    :initial-contacts="initialDeliveryContacts"
                                    multiple>
                    </contact-select>
                </b-form-group>
            </b-form-row>
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <b-form-group label="Distribution CC List"
                              label-for="deliveryContactCcIds"
                              class="col">
                    <contact-select name="deliveryContactCcIds"
                                    :api-url="$urlHelper.getApiUrl('contact','delivery')"
                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                    :client-id="orderingClientId"
                                    :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                    v-model="currentDeliveryContactCcIds"
                                    :initial-contacts="initialDeliveryContacts"
                                    multiple>
                    </contact-select>
                </b-form-group>
            </b-form-row>
            <b-form-row v-if="!hideDistributionList && !serviceTypeHideDistributionList">
                <b-form-group label="Distribution BCC List"
                              label-for="deliveryContactBccIds"
                              class="col">
                    <contact-select name="deliveryContactBccIds"
                                    :api-url="$urlHelper.getApiUrl('contact','delivery')"
                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                    :client-id="orderingClientId"
                                    :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                    v-model="currentDeliveryContactBccIds"
                                    :initial-contacts="initialDeliveryContacts"
                                    multiple>
                    </contact-select>
                </b-form-group>
            </b-form-row>
            <!--Comments-->
            <b-form-row>
                <b-form-group label="Comments"
                              label-for="comments"
                              class="col-md-12">
                    <b-form-textarea name="comments"
                                     rows="3"
                                     max-rows="6"
                                     :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                                     v-model="comments">
                    </b-form-textarea>
                </b-form-group>
            </b-form-row>
            <!--Attachments-->
            <attachments v-model="currentAttachments"
                         :all-attachment-classifications="allAttachmentClassifications"
                         :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC"
                         attachment-classification-selection="required"
                         :can-add-attachments="(allAttachmentClassifications != null) && (allAttachmentClassifications.length > 0)"
                         v-if="canViewAttachments">
            </attachments>

            <b-form-row>
                <b-form-group class="col-md-4">
                    <b-form-checkbox name="awaitingFurtherAttachments"
                                     v-model="awaitingFurtherAttachments"
                                     class="mb-2"
                                     switch
                                     :disabled="readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC">
                        Awaiting Further Attachments
                    </b-form-checkbox>
                </b-form-group>
            </b-form-row>

            <validation-summary ref="validationSummary" :vee-validate-observer-errors="vo.errors"></validation-summary>

            <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                <div class="btn-group" role="group">
                    <b-button variant="outline-success"
                              v-on:click.prevent="trySaveOrder('close')"
                              v-if="!readonly && canEditOrderRequest && (orderRequest.submittedDateTimeUTC == null)">
                        <i class="fas fa-check mr-1"></i>Save and Close
                    </b-button>
                    <b-button variant="outline-success"
                              v-on:click.prevent="showTerms"
                              v-if="!readonly && canEditOrderRequest && (orderRequest.submittedDateTimeUTC == null) && (awaitingFurtherAttachments == false)">
                        <i class="fas fa-check mr-1"></i>Submit Order Request
                    </b-button>
                    <b-button :href="$urlHelper.getPageUrl('OrderRequests/Index?initialTab=Interview')"
                              :variant="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? 'outline-secondary' : 'outline-danger'">
                        <i :class="(readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? 'fas fa-arrow-left mr-1' : 'fas fa-times mr-1'"></i>
                        {{ (readonly || !canEditOrderRequest || orderRequest.submittedDateTimeUTC) ? 'Back' : 'Cancel' }}
                    </b-button>
                </div>
            </div>
        </validation-observer>

        <b-input-group size="sm" prepend="Created At" class="mt-2" v-if="!isUuidNullOrEmpty(orderRequest.clientOrderRequestId)">
            <b-form-input :value="orderRequest.createdDateTimeUTC | formatDateTime('DD/MM/YYYY h:mmA')" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
            <b-form-input :value="orderRequest.createdByUserAccount.displayName" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">Last Update At</b-input-group-prepend>
            <b-form-input :value="orderRequest.lastUpdateDateTimeUTC | formatDateTime('DD/MM/YYYY h:mmA')" readonly></b-form-input>
            <b-input-group-prepend is-text class="input-group-append">By</b-input-group-prepend>
            <b-form-input :value="(orderRequest.lastUpdateByUserAccount != null) ? orderRequest.lastUpdateByUserAccount.displayName : null" readonly></b-form-input>
        </b-input-group>

        <validation-observer v-slot="vo1" slim>
            <b-modal size="md"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     title="Submit Order Request"
                     v-model="acceptTermsDialogShown"
                     v-on:ok.prevent="vo1.handleSubmit(trySaveOrder('submit'))">
                <!--v-on:ok="trySaveOrder('submit')">-->
                <template #default>
                    <b-form-row>
                        <validation-provider :rules="{ required: { allowFalse: false } }"
                                             name="Accept Terms"
                                             v-slot="{ valid, errors }"
                                             slim
                                             :custom-messages="{ required: 'You have to agree to the terms of use' }">
                            <b-form-group>
                                <b-form-checkbox name="acceptTerms"
                                                 class="mb-2"
                                                 switch
                                                 v-model="acceptTerms"
                                                 :state="errors[0] ? false : null">
                                    By submitting this order, I agree to the <a href="https://www.epiqglobal.com/en-au/terms-of-use-au" target="_blank">Terms of Use</a>
                                </b-form-checkbox>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                    </b-form-row>
                </template>
                <template #modal-footer="{ ok, cancel }">
                    <button type="button" class="btn btn-secondary" @click="cancel()">No, Do Not Submit</button>
                    <button type="button" class="btn btn-primary" @click="ok()">Yes, Agree and Submit</button>
                </template>
            </b-modal>
        </validation-observer>

    </div>
</template>

<script>
    import Vue from 'vue';
    import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue';

    import axios from 'axios';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import UuidMixins from '../../mixins/UuidMixins';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required, required_if } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';

    extend('required', required);
    extend('required_if', required_if);
    localize('en', en);

    Vue.use(BootstrapVue);
    Vue.use(BootstrapVueIcons);

    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);

    export default {
        name: "InterviewOrderRequestDetails",
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        filters: DateTimeMixin.filter,
        props: {
            readonly: {
                type: Boolean,
                default: false
            },
            canEditOrderRequest: {
                type: Boolean,
                default: true
            },
            canViewAttachments: {
                type: Boolean,
                default: true
            },
            orderRequest: {
                type: Object,
                required: true
            },
            initialBillingContact: {
                type: Object,
                default: null
            },
            promptForBillingContact: {
                type: Boolean,
                default: null
            },
            allInterviewOrganisations: {
                type: Array,
                default: null
            },
            initialServiceTypes: {
                type: Array,
                default: null
            },
            initialServiceDelays: {
                type: Array,
                default: null
            },
            deliveryContactIds: {
                type: Array,
                default: null
            },
            deliveryContactCcIds: {
                type: Array,
                default: null
            },
            deliveryContactBccIds: {
                type: Array,
                default: null
            },
            initialDeliveryContacts: {
                type: Array,
                default: null
            },
            allAttachmentClassifications: {
                type: Array,
                required: true
            },
            attachments: {
                type: Array,
                default: null
            },
            selectableClientBranches: {
                type: Array,
                default: null
            },
            allTimeZones: {
                type: Array,
                default: null
            },
        },
        data() {
            return {
                orderingClientId: this.orderRequest.orderingClient.clientId,
                orderingClient: this.orderRequest.orderingClient.name,
                orderingClientBranchId: this.isUuidNullOrEmpty(this.orderRequest.orderingClientBranchId) ? null : this.orderRequest.orderingClientBranchId,
                orderingClientContactId: this.orderRequest.orderingClientContact.contactId,
                orderingClientContact: this.orderRequest.orderingClientContact.name,
                clientReference: this.orderRequest.clientReference,
                separateBillingContact: (this.isUuidNullOrEmpty(this.orderRequest.clientOrderRequestId) && this.promptForBillingContact) || (!this.isUuidNullOrEmpty(this.orderRequest.clientOrderRequestId) && (this.initialBillingContact != null)),
                billingContactId: this.orderRequest.billingContactId,

                interviewOrganisationId: this.orderRequest.interviewOrganisationId ? this.orderRequest.interviewOrganisationId : null,
                interviewDateTime: this.orderRequest.interviewDateTime ? this.orderRequest.interviewDateTime : null,
                interviewTimezoneId: this.orderRequest.interviewTimezoneId,
                interviewLengthInSeconds: this.orderRequest.interviewLengthInSeconds ? this.orderRequest.interviewLengthInSeconds : null,
                interviewMatterOrCaseReference: this.orderRequest.interviewMatterOrCaseReference ? this.orderRequest.interviewMatterOrCaseReference : null,
                interviewLocation: this.orderRequest.interviewLocation,
                interviewers: this.orderRequest.interviewers,
                interviewees: this.orderRequest.interviewees,

                currentErrors: [],
                dialogOpen: false,
                serviceTypeId: this.isUuidNullOrEmpty(this.orderRequest.serviceTypeId) ? null : this.orderRequest.serviceTypeId,
                currentSelectableServiceTypes: this.initialServiceTypes,
                serviceTypeRequiresServiceDelay: (this.orderRequest.serviceType == null) ? false : this.orderRequest.serviceType.requiresServiceDelay,
                serviceDelayId: this.isUuidNullOrEmpty(this.orderRequest.serviceDelayId) ? null : this.orderRequest.serviceDelayId,
                currentSelectableServiceDelays: this.initialServiceDelays,
                currentDeliveryContactIds: this.deliveryContactIds,
                currentDeliveryContactCcIds: this.deliveryContactCcIds,
                currentDeliveryContactBccIds: this.deliveryContactBccIds,
                comments: this.orderRequest.comments,
                currentAttachments: this.attachments,
                awaitingFurtherAttachments: this.orderRequest.awaitingFurtherAttachments,

                acceptTermsDialogShown: false,
                acceptTerms: false,
            };
        },
        watch: {
            separateBillingContact: function () {
                if (!this.separateBillingContact) {
                    this.billingContactId = null;
                }
            },
        },

        computed: {
            mandatoryContactProperties: function () {
                if (!this.$config.enableRegionStateOptions) {
                    return ['address', 'suburb', 'postcode', 'country'];
                }
                else {
                    return ['address', 'suburb', 'state', 'postcode', 'country'];
                }
            }
        },
        methods: {
            interviewOrganisationSelected: function () {
                this.serviceTypeId = null;
                this.serviceDelayId = null;
                this.reloadServiceTypes();
                this.reloadServiceDelays();
            },
            reloadServiceTypes: function () {
                if (this.interviewOrganisationId) {
                    var url = this.$urlHelper.getApiUrl('servicetype', 'byintervieworganisation', [this.interviewOrganisationId]);
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceTypes = response.data;
                            Vue.nextTick().then(() => {
                                this.$refs.serviceTypeSelect.refresh();
                                this.$refs.serviceTypeSelectValidator.reset();
                            });
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceTypes = null;
                }
            },
            reloadServiceDelays: function () {
                if (this.interviewOrganisationId) {
                    var url = this.$urlHelper.getApiUrl('servicedelay', 'byintervieworganisation', [this.interviewOrganisationId]);
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceDelays = response.data;
                            Vue.nextTick().then(() => {
                                if (typeof this.$refs.serviceDelaySelect != "undefined")
                                    this.$refs.serviceDelaySelect.refresh();
                            });
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceDelays = null;
                }
            },
            serviceTypeSelected: function (serviceType) {
                if (serviceType != null) {
                    this.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                    if (!this.serviceTypeRequiresServiceDelay) {
                        this.serviceDelayId = null;
                    }

                }
                else {
                    this.serviceTypeRequiresServiceDelay = false;
                    this.serviceDelayId = null;
                }
            },
            trySaveOrder: function (actionAfterSave) {
                if (actionAfterSave == "submit" && this.acceptTerms != true) {
                    return;
                }

                var thisVueApp = this;
                this.$refs.validationObserver
                    .validate()
                    .then(function (isValid) {
                        if (isValid) thisVueApp.saveOrderRequest(actionAfterSave);
                        else thisVueApp.$refs.validationSummary.activate();
                    });
            },
            saveOrderRequest: function (actionAfterSave) {
                //console.log('courtOrder: ' + this.orderingClientId);
                var data = {
                    clientOrderRequestId: this.orderRequest.clientOrderRequestId,
                    orderRequestNumber: this.orderRequest.orderRequestNumber,
                    orderingClientId: this.orderingClientId,
                    orderingClientBranchId: this.orderingClientBranchId,
                    orderingClientContactId: this.orderingClientContactId,
                    interviewOrganisationId: this.interviewOrganisationId,
                    interviewDateTime: this.parseDateTime(this.formatDateTime(this.interviewDateTime, 'YYYY-MM-DDTHH:mm:ss')),
                    interviewTimezoneId: this.interviewTimezoneId,
                    interviewLengthInSeconds: this.interviewLengthInSeconds,
                    interviewMatterOrCaseReference: this.interviewMatterOrCaseReference,
                    interviewLocation: this.interviewLocation,
                    interviewers: this.interviewers,
                    interviewees: this.interviewees,
                    clientReference: this.clientReference,
                    billingContactId: this.billingContactId,
                    serviceTypeId: this.serviceTypeId,
                    serviceDelayId: this.serviceDelayId,
                    deliveryContactIds: this.currentDeliveryContactIds,
                    deliveryContactCcIds: this.currentDeliveryContactCcIds,
                    deliveryContactBccIds: this.currentDeliveryContactBccIds,
                    comments: this.comments,
                    attachments: [],
                    awaitingFurtherAttachments: this.awaitingFurtherAttachments,
                };

                if (this.currentAttachments != null) {
                    this.currentAttachments.forEach(function (attachment) {
                        data.attachments.push({ fileId: attachment.fileId, classification: attachment.classificationString, copyOnSave: attachment.copyOnSave });
                    });
                }

                if (actionAfterSave == 'submit') {
                    data.clientOrderRequestStatus = 'Submitted';
                }

                this.currentErrors = [];
                var url = this.$urlHelper.getApiUrl('ClientOrderRequest', 'interview');
                var thisVueApp = this;
                axios
                    .request(
                        {
                            method: (this.orderId == null) ? 'post' : 'put',
                            url: url,
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        if (actionAfterSave == 'submit') {
                            window.location.replace(thisVueApp.$urlHelper.getPageUrl("OrderRequests/Index?initialTab=Interview"));
                        }
                        else if (actionAfterSave == 'close') {
                            window.location.replace(thisVueApp.$urlHelper.getPageUrl("OrderRequests/Index?initialTab=Interview"));
                        }
                    })
                    .catch((ex) => {
                        thisVueApp.currentErrors = thisVueApp.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (thisVueApp.currentErrors.length != 0) {
                            thisVueApp.showErrorsToast(thisVueApp.currentErrors);
                        }
                    });
            },
            showTerms: function () {
                this.acceptTerms = false;
                this.acceptTermsDialogShown = true;
            },
        },
        mounted: function () {
            Vue.nextTick().then(() => {
                this.$refs.interviewDateValidator.reset();
            });
        }
    };
</script>



